import { Input } from '@oysterjs/ui/input';
import { CheckboxGroup } from '@oysterjs/ui/checkbox';
import { Select } from '@oysterjs/ui/select';
import { Textarea } from '@oysterjs/ui/textarea';
import {
  AgencyUserState,
  TriggerEmailRuleKind as Kind,
  TriggerEmailTextOperator as TextOperator,
  TriggerEmailPhraseField as PhraseField,
  TriggerEmailAddressKind as AddressKind,
  TriggerEmailRecipientField as RecipientField,
  TriggerEmailTimeOperator as TimeOperator,
  TriggerEmailWeekday as Weekday,
  type TriggerEmailAddressMatchInput,
  type TriggerEmailRuleInput,
  type TriggerEmailTextMatchInput
} from '~/api/graphql';
import { emptyTextMatch } from '~/api/data/triggers/config';
import {
  compareEmailWeekdays,
  EMAIL_WEEKDAYS,
  type EmailErrors,
  type EmailOptions
} from '~/api/data/triggers/email-config';
import {
  BuilderField,
  Check,
  ValuesField,
  ValueModeField,
  NumberField,
  humanize,
  toggleValue,
  PathErrors,
  minuteText,
  parseMinute,
  Help
} from '~/components/triggers/email-builder/fields';

export { minuteText } from '~/components/triggers/email-builder/fields';
export const TEXT_OPERATORS: Record<TextOperator, string> = {
  CONTAINS: 'Contains text',
  EQUALS: 'Equals text',
  STARTS_WITH: 'Starts with text',
  ENDS_WITH: 'Ends with text',
  REGEX: 'Matches a pattern (regex)'
};
export const TextMatchEditor = ({
  value,
  onChange,
  path,
  errors,
  compact = false
}: {
  value: TriggerEmailTextMatchInput;
  onChange: (value: TriggerEmailTextMatchInput) => void;
  path: string;
  errors: EmailErrors;
  /** The condition row already chose the operator, so the editor omits it. */
  compact?: boolean;
}) => (
  <div className="max-w-xl space-y-6">
    <PathErrors path={path} errors={errors} />
    {!compact && (
      <BuilderField label="Text or regex" path={`${path}.operator`} errors={errors}>
        {(props) => (
          <Select
            {...props}
            value={value.operator}
            onChange={(event) =>
              onChange({ ...value, operator: event.target.value as TextOperator })
            }
          >
            {Object.entries(TEXT_OPERATORS).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </Select>
        )}
      </BuilderField>
    )}
    <ValuesField
      label={value.operator === TextOperator.Regex ? 'Regular expressions' : 'Phrases'}
      path={`${path}.values`}
      errors={errors}
      values={value.values}
      onChange={(values) => onChange({ ...value, values })}
    />
    <ValueModeField
      path={`${path}.valueMode`}
      errors={errors}
      value={value.valueMode}
      onChange={(valueMode) => onChange({ ...value, valueMode })}
    />
    <Check
      label="Case sensitive"
      checked={value.caseSensitive ?? false}
      onChange={(caseSensitive) => onChange({ ...value, caseSensitive })}
    />
    {value.operator === TextOperator.Regex && (
      <Help>The pattern is checked when you save. Use standard regular expression syntax.</Help>
    )}
  </div>
);
const AddressEditor = ({
  value,
  onChange,
  path,
  errors,
  options,
  label,
  compact = false
}: {
  value: TriggerEmailAddressMatchInput;
  onChange: (value: TriggerEmailAddressMatchInput) => void;
  path: string;
  errors: EmailErrors;
  options: EmailOptions;
  label: string;
  /** The condition row already chose the address type, so the editor omits it. */
  compact?: boolean;
}) => {
  const lookup = value.kind === AddressKind.User || value.kind === AddressKind.AccessProfile;
  const choices =
    value.kind === AddressKind.User
      ? options.users.map((user) => ({
          id: user.id,
          label: `${user.name} (${user.email})`,
          disabled: user.state !== AgencyUserState.Active
        }))
      : options.profiles.map((profile) => ({
          id: profile.id,
          label: profile.name,
          disabled: false
        }));
  return (
    <div className="max-w-xl space-y-6">
      <PathErrors path={path} errors={errors} />
      {!compact && (
        <BuilderField label={`${label} matches`} path={`${path}.kind`} errors={errors}>
          {(props) => (
            <Select
              {...props}
              value={value.kind}
              onChange={(event) =>
                onChange({
                  ...value,
                  kind: event.target.value as AddressKind,
                  values: [],
                  includeSubdomains: false
                })
              }
            >
              {Object.values(AddressKind).map((kind) => (
                <option key={kind} value={kind}>
                  {humanize(kind)}
                </option>
              ))}
            </Select>
          )}
        </BuilderField>
      )}
      {lookup ? (
        <BuilderField
          label={value.kind === AddressKind.User ? 'Agency users' : 'Access profiles'}
          path={`${path}.values`}
          errors={errors}
          description="Select active agency users or access profiles."
        >
          {(props) => (
            <CheckboxGroup
              id={props.id}
              role="group"
              aria-labelledby={props['aria-labelledby']}
              aria-describedby={props['aria-describedby']}
              aria-invalid={props['aria-invalid']}
            >
              {!choices.length && (
                <p role="status">
                  No {value.kind === AddressKind.User ? 'users' : 'access profiles'} are available.
                </p>
              )}
              {choices.map((choice) => (
                <Check
                  key={choice.id}
                  label={choice.label}
                  checked={value.values.includes(choice.id)}
                  disabled={
                    (choice.disabled || value.values.length >= 32) &&
                    !value.values.includes(choice.id)
                  }
                  onChange={(checked) =>
                    onChange({ ...value, values: toggleValue(value.values, choice.id, checked) })
                  }
                />
              ))}
              {value.values
                .filter((id) => !choices.some((choice) => choice.id === id))
                .map((id) => (
                  <Check
                    key={id}
                    label="Unavailable selection (remove it to choose another)"
                    checked
                    onChange={() =>
                      onChange({ ...value, values: value.values.filter((item) => item !== id) })
                    }
                  />
                ))}
            </CheckboxGroup>
          )}
        </BuilderField>
      ) : (
        <ValuesField
          label={
            value.kind === AddressKind.Address
              ? `${label} addresses`
              : `${label} domains (without @)`
          }
          values={value.values}
          path={`${path}.values`}
          errors={errors}
          onChange={(values) => onChange({ ...value, values })}
        />
      )}
      <ValueModeField
        value={value.valueMode}
        path={`${path}.valueMode`}
        errors={errors}
        onChange={(valueMode) => onChange({ ...value, valueMode })}
      />
      {value.kind === AddressKind.Domain && (
        <Check
          label="Include subdomains"
          checked={value.includeSubdomains ?? false}
          onChange={(includeSubdomains) => onChange({ ...value, includeSubdomains })}
        />
      )}
    </div>
  );
};
const DAY_LABELS: Record<Weekday, string> = {
  [Weekday.Monday]: 'Monday',
  [Weekday.Tuesday]: 'Tuesday',
  [Weekday.Wednesday]: 'Wednesday',
  [Weekday.Thursday]: 'Thursday',
  [Weekday.Friday]: 'Friday',
  [Weekday.Saturday]: 'Saturday',
  [Weekday.Sunday]: 'Sunday'
};
export const ConditionEditor = ({
  node,
  onChange,
  path,
  errors,
  options,
  compact = false
}: {
  node: TriggerEmailRuleInput;
  onChange: (node: TriggerEmailRuleInput) => void;
  path: string;
  errors: EmailErrors;
  options: EmailOptions;
  /** Hides settings the condition row already carries: field, operator and address type. */
  compact?: boolean;
}) => {
  if (node.kind === Kind.Phrase && node.phrase) {
    const value = node.phrase;
    return (
      <div className="max-w-xl space-y-6">
        {(!compact || value.field !== PhraseField.Subject) && (
          <Help>
            Body matching includes quoted replies and forwards.
            {!compact &&
              ' When matching all values, each phrase may match either configured field.'}
          </Help>
        )}
        <PathErrors path={`${path}.phrase`} errors={errors} />
        {!compact && (
          <BuilderField label="Look in" path={`${path}.phrase.field`} errors={errors}>
            {(props) => (
              <Select
                {...props}
                value={value.field}
                onChange={(event) =>
                  onChange({
                    ...node,
                    phrase: { ...value, field: event.target.value as PhraseField }
                  })
                }
              >
                <option value={PhraseField.Subject}>Subject</option>
                <option value={PhraseField.Body}>Full email body</option>
                <option value={PhraseField.SubjectOrBody}>Subject or body</option>
              </Select>
            )}
          </BuilderField>
        )}
        <TextMatchEditor
          value={value.match}
          path={`${path}.phrase.match`}
          errors={errors}
          compact={compact}
          onChange={(match) => onChange({ ...node, phrase: { ...value, match } })}
        />
      </div>
    );
  }
  if (node.kind === Kind.Sender && node.sender)
    return (
      <AddressEditor
        label="Sender"
        value={node.sender}
        path={`${path}.sender`}
        errors={errors}
        options={options}
        compact={compact}
        onChange={(sender) => onChange({ ...node, sender })}
      />
    );
  if (node.kind === Kind.Recipient && node.recipient) {
    const value = node.recipient;
    return (
      <div className="max-w-xl space-y-6">
        <PathErrors path={`${path}.recipient`} errors={errors} />
        {!compact && (
          <BuilderField label="Recipient field" path={`${path}.recipient.field`} errors={errors}>
            {(props) => (
              <Select
                {...props}
                value={value.field}
                onChange={(event) =>
                  onChange({
                    ...node,
                    recipient: { ...value, field: event.target.value as RecipientField }
                  })
                }
              >
                <option value={RecipientField.To}>To</option>
                <option value={RecipientField.Cc}>Cc</option>
                <option value={RecipientField.ToOrCc}>To or Cc</option>
              </Select>
            )}
          </BuilderField>
        )}
        <AddressEditor
          label="Recipient"
          value={value.match}
          path={`${path}.recipient.match`}
          errors={errors}
          options={options}
          compact={compact}
          onChange={(match) => onChange({ ...node, recipient: { ...value, match } })}
        />
      </div>
    );
  }
  if (node.kind === Kind.Time && node.time) {
    const value = node.time;
    return (
      <div className="max-w-xl space-y-6">
        <Help>
          Uses the trigger's chosen time zone and the original email received time. Between includes
          the start, excludes the end, and can cross midnight.
        </Help>
        <PathErrors path={`${path}.time`} errors={errors} />
        {!compact && (
          <BuilderField label="Time matches" path={`${path}.time.operator`} errors={errors}>
            {(props) => (
              <Select
                {...props}
                value={value.operator}
                onChange={(event) => {
                  const operator = event.target.value as TimeOperator;
                  onChange({
                    ...node,
                    time: {
                      ...value,
                      operator,
                      endMinute:
                        operator === TimeOperator.Between ? (value.endMinute ?? null) : null
                    }
                  });
                }}
              >
                {Object.values(TimeOperator).map((operator) => (
                  <option key={operator} value={operator}>
                    {humanize(operator)}
                  </option>
                ))}
              </Select>
            )}
          </BuilderField>
        )}
        <BuilderField
          label={value.operator === TimeOperator.Between ? 'Start time' : 'Time'}
          path={`${path}.time.minute`}
          errors={errors}
        >
          {(props) => (
            <Input
              {...props}
              type="time"
              step={60}
              value={minuteText(value.minute)}
              onChange={(event) =>
                onChange({ ...node, time: { ...value, minute: parseMinute(event.target.value) } })
              }
            />
          )}
        </BuilderField>
        {value.operator === TimeOperator.Between && (
          <>
            {value.endMinute !== 1440 && (
              <BuilderField label="End time" path={`${path}.time.endMinute`} errors={errors}>
                {(props) => (
                  <Input
                    {...props}
                    type="time"
                    step={60}
                    value={minuteText(value.endMinute ?? -1)}
                    onChange={(event) =>
                      onChange({
                        ...node,
                        time: { ...value, endMinute: parseMinute(event.target.value) }
                      })
                    }
                  />
                )}
              </BuilderField>
            )}
            <Check
              label="End at midnight (24:00)"
              checked={value.endMinute === 1440}
              onChange={(checked) =>
                onChange({ ...node, time: { ...value, endMinute: checked ? 1440 : null } })
              }
            />
            {value.endMinute === 1440 && (
              <PathErrors path={`${path}.time.endMinute`} errors={errors} />
            )}
          </>
        )}
        <BuilderField
          label="Days"
          path={`${path}.time.weekdays`}
          errors={errors}
          description="Overnight windows belong to their start day."
        >
          {(props) => (
            <CheckboxGroup
              id={props.id}
              role="group"
              aria-labelledby={props['aria-labelledby']}
              aria-describedby={props['aria-describedby']}
              aria-invalid={props['aria-invalid']}
            >
              {EMAIL_WEEKDAYS.map((day) => (
                <Check
                  key={day}
                  label={DAY_LABELS[day]}
                  checked={value.weekdays.includes(day)}
                  onChange={(checked) =>
                    onChange({
                      ...node,
                      time: {
                        ...value,
                        weekdays: toggleValue(value.weekdays, day, checked).sort(
                          compareEmailWeekdays
                        )
                      }
                    })
                  }
                />
              ))}
            </CheckboxGroup>
          )}
        </BuilderField>
      </div>
    );
  }
  if (node.kind === Kind.Attachment && node.attachment) {
    const value = node.attachment;
    const bounds = ['minimumCount', 'maximumCount', 'minimumBytes', 'maximumBytes'] as const;
    const labels = {
      minimumCount: 'Minimum number of attachments',
      maximumCount: 'Maximum number of attachments (optional)',
      minimumBytes: 'Minimum file size (optional)',
      maximumBytes: 'Maximum file size (optional)'
    };
    return (
      <div className="max-w-xl space-y-6">
        <PathErrors path={`${path}.attachment`} errors={errors} />
        <Help>
          Counts attachments, not images embedded in the email body. A single file has to meet every
          setting you fill in, and file types and extensions are alternatives: a file matches if it
          fits any one entry. File sizes are in bytes (1,000,000 is about 1 MB).
        </Help>
        <div className="grid gap-3 sm:grid-cols-2">
          {bounds.map((key) => (
            <NumberField
              key={key}
              label={labels[key]}
              path={`${path}.attachment.${key}`}
              errors={errors}
              value={value[key]}
              nullable={key !== 'minimumCount'}
              max={key.endsWith('Count') ? 2147483647 : Number.MAX_SAFE_INTEGER}
              onChange={(next) => onChange({ ...node, attachment: { ...value, [key]: next } })}
            />
          ))}
        </div>
        {errors[`${path}.attachment.bytes`] && (
          <p role="alert">{errors[`${path}.attachment.bytes`]}</p>
        )}
        <ValuesField
          label="File types (optional)"
          description="For example application/pdf or image/png."
          values={value.mimeTypes ?? []}
          path={`${path}.attachment.mimeTypes`}
          errors={errors}
          onChange={(mimeTypes) => onChange({ ...node, attachment: { ...value, mimeTypes } })}
        />
        <ValuesField
          label="File extensions (optional)"
          description="For example pdf or docx, without the dot."
          values={value.extensions ?? []}
          path={`${path}.attachment.extensions`}
          errors={errors}
          onChange={(extensions) => onChange({ ...node, attachment: { ...value, extensions } })}
        />
        <Check
          label="Match attachment name"
          checked={!!value.name}
          onChange={(checked) =>
            onChange({ ...node, attachment: { ...value, name: checked ? emptyTextMatch() : null } })
          }
        />
        {value.name && (
          <TextMatchEditor
            value={value.name}
            path={`${path}.attachment.name`}
            errors={errors}
            onChange={(name) => onChange({ ...node, attachment: { ...value, name } })}
          />
        )}
      </div>
    );
  }
  if (node.kind === Kind.Prompt && node.prompt)
    return (
      <>
        <PathErrors path={`${path}.prompt`} errors={errors} />
        <BuilderField
          label="Condition prompt"
          path={`${path}.prompt.prompt`}
          errors={errors}
          description="Describe what Cara should look for, in up to about 2,000 characters, with up to three of these per trigger. If Cara can’t decide, the email doesn’t count as a match, even for a “does not” condition."
        >
          {(props) => (
            <Textarea
              {...props}
              rows={3}
              value={node.prompt!.prompt}
              onChange={(event) => onChange({ ...node, prompt: { prompt: event.target.value } })}
            />
          )}
        </BuilderField>
      </>
    );
  return (
    <p role="alert">This condition is unsupported. Reload the saved version before editing.</p>
  );
};
